import { SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { isEmpty } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import BuyerInfoModal from 'src/components/BuyerInfoModal';
import LoadingPanel from 'src/components/LoadingPanel';
import Accordion from 'src/components/design-system/Accordion';
import Switch from 'src/components/design-system/Switch';
import { exportInvoiceReadOnly } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { SupplierInfo } from 'src/services/useInvoiceDetailsApi';
import 'src/style/grid/grid-override.scss';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';
import ApId from './invoiceInfo/ApId';
import BudgetPeriod from './invoiceInfo/BudgetPeriod';
import Discount from './invoiceInfo/Discount';
import GrandTotal from './invoiceInfo/GrandTotal';
import InvoiceDate from './invoiceInfo/InvoiceDate';
import InvoiceNote from './invoiceInfo/InvoiceNote';
import InvoiceNumber from './invoiceInfo/InvoiceNumber';
import PoNumber from './invoiceInfo/PoNumber';
import ReceivedDate from './invoiceInfo/ReceivedDate';
import Shipping from './invoiceInfo/Shipping';
import SupplierInfoModal from './invoiceInfo/SupplierInfoModal';
import SupplierName from './invoiceInfo/SupplierName';
import Taxes from './invoiceInfo/Taxes';

interface InvoiceInfoProps {}

const InvoiceInfo: FC<InvoiceInfoProps> = () => {
  const setInvoice = useContextSelector(InvoiceContext, (value) => value.setInvoice);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const poDetails = useContextSelector(InvoiceContext, (value) => value.poDetails);

  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );

  const handleBooleanChange = useCallback(
    (key: string) => (value: boolean) => {
      setInvoice((prev) => ({ ...prev, [key]: value }));
    },
    [setInvoice]
  );

  const handleChange = (e: SwitchChangeEvent) => {
    handleBooleanChange('prepaid')(e.value);
  };

  const invoiceStatus: string = useMemo(
    () => `${invoice.status} - ${invoice?.isOffline ? 'Offline' : 'Online'}`,
    [invoice?.isOffline, invoice.status]
  );

  const prepaidLabel = useMemo(
    () =>
      `This ${title} has already been paid. Do not make this document available for export or transmission to Accounts Payable.`,
    [title]
  );

  const canExportInvoiceReadOnly = usePermission({ permissions: exportInvoiceReadOnly });

  return (
    <Accordion
      title={`Details for ${title} #${invoice?.invoiceNumber || ''}`}
      defaultExpanded
      expandAlways
    >
      {invoice ? (
        <Tooltip anchorElement="target" position="bottom" parentTitle>
          {!isEmpty(invoice) && (
            <div className="p-1 overflow-auto">
              <div className="grid  grid-cols-2 grid-rows-8 md:grid-cols-3 md:grid-rows-6 lg:grid-cols-5 lg:grid-rows-4 xl:grid-cols-6 xl:grid-rows-3 gap-3">
                <InvoiceNumber />
                <PoNumber />
                <div
                  title={String(invoice?.receivingReceiptId ? invoice?.receivingReceiptId : '')}
                  className="p-1 text-[13px]"
                >
                  <p className="font-bold pb-1"> Receipt # </p>
                  <p>{invoice?.receivingReceiptId} </p>
                </div>
                <SupplierInfoModal supplierInfo={poDetails?.supplier as SupplierInfo} />
                <BuyerInfoModal buyerInfo={poDetails?.buyer} />
                <InvoiceDate />
                <ReceivedDate />
                <div title={invoiceStatus} className="p-1 text-[13px]">
                  <p className="font-bold pb-1"> Status </p>
                  <p>{invoiceStatus}</p>
                </div>
                <BudgetPeriod />
                <ApId />
                <SupplierName />
                <InvoiceNote />
                <Discount />
                <Shipping />
                <Taxes />
                <GrandTotal />
              </div>
              {invoice?.isOffline && (
                <div className="mt-1 flex items-center gap-2 font-bold">
                  <span
                    className="shrink-0"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-html={
                      !isInvoiceEditDisabled && canExportInvoiceReadOnly
                        ? 'This feature is currently set to read-only mode. Contact customer support to enable editing.'
                        : ''
                    }
                  >
                    <Switch
                      value={invoice?.prepaid}
                      onChange={handleChange}
                      onLabel="YES"
                      offLabel="NO"
                      disabled={isInvoiceEditDisabled || canExportInvoiceReadOnly}
                    />
                  </span>
                  <span className="min-w-0">{prepaidLabel}</span>
                </div>
              )}
            </div>
          )}
        </Tooltip>
      ) : (
        <LoadingPanel />
      )}
    </Accordion>
  );
};
export default InvoiceInfo;
